import React from "react";
import { ArrowRight, CreditCard, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getActivationCtaLabel,
  getActivationProfileRoute,
  getActivationRoleFeatures,
  getActivationSupportCopy,
  getAssignedPlan,
  getPlanLabel,
  getRoleLabel,
  getStatusLabel,
  getTrialDaysCopy,
  isActivationEligibleRole,
  ACTIVATION_PROFILE_CTA_BY_ROLE,
} from "../../constants/plans";
import { getActivationJourneySteps } from "../../constants/activationJourney";

const ActivationGate = ({
  user,
  subscriptionInfo,
  handleUpgrade,
  isActivatingSubscription,
  navigate,
}) => {
  if (!user || !isActivationEligibleRole(user.role)) return null;

  const features = getActivationRoleFeatures(user.role);
  const profileRoute = getActivationProfileRoute(user.role);
  const profileCtaLabel = ACTIVATION_PROFILE_CTA_BY_ROLE[user.role] || "Completar perfil";
  const assignedPlan = getAssignedPlan(user);
  const hasUsedTrial = !!user?.trial_used;
  const activationCtaLabel = getActivationCtaLabel(user);
  const activationSupportCopy = getActivationSupportCopy(user);
  const currentStatus = subscriptionInfo?.status || "pending";
  const activationJourneySteps = getActivationJourneySteps({
    planLabel: getPlanLabel(assignedPlan),
    trialDaysCopy: getTrialDaysCopy(),
    hasUsedTrial,
  });

  return (
    <div
      className="bg-background border border-border rounded-2xl overflow-hidden mb-6"
      data-testid="activation-gate"
    >
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 text-white p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] opacity-70 mb-2">
              Activación pendiente
            </p>
            <h2
              className="text-2xl md:text-3xl font-bold"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Activa tu {getPlanLabel(assignedPlan)}
            </h2>
            <p className="text-sm md:text-base text-slate-200 mt-2 max-w-2xl">
              Tu cuenta ya fue creada como {getRoleLabel(user.role)}. Completa
              la activación para
              {hasUsedTrial
                ? " habilitar tu suscripción y desbloquear"
                : ` empezar gratis con tu prueba de ${getTrialDaysCopy()} y desbloquear`}
              todas las funciones de tu rol.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20">
              {getRoleLabel(user.role)}
            </span>
            <span className="px-3 py-1 rounded-full bg-amber-400/20 text-amber-200 border border-amber-300/30">
              Estado: {getStatusLabel(currentStatus)}
            </span>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-[1.3fr_0.9fr] gap-6">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Se habilita al activar</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            {activationJourneySteps.map((step) => (
              <div
                key={step.key}
                className="rounded-xl border border-border bg-muted/20 p-4"
              >
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{step.eyebrow}</p>
                <p className="text-sm font-medium">{step.title}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {features.map((feature) => (
              <div
                key={feature}
                className="rounded-xl border border-border bg-muted/30 p-4 text-sm font-medium"
              >
                {feature}
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-green-600">✓</span>
              Se mantiene disponible
            </h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>• Ver y gestionar el inventario que ya publicaste</p>
              <p>• Editar tu perfil (nombre, contacto)</p>
              <p>• Acceder a Mi Cuenta para reactivar o revisar tu estado</p>
              <p>• Explorar propiedades y desarrollos del marketplace</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-muted/20 p-5">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Siguientes pasos</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground mb-5">
            <p>1. Completa tu perfil inicial.</p>
            <p>2. {activationSupportCopy}</p>
            <p>3. Regresa al Dashboard para operar con tus herramientas premium.</p>
          </div>
          <div className="rounded-lg border border-border bg-background px-4 py-3 text-sm text-muted-foreground mb-5">
            {hasUsedTrial
              ? "La prueba ya se consumió en esta cuenta. El flujo te llevará directo a reactivar o continuar la suscripción correspondiente."
              : `Si esta es tu primera activación elegible, Stripe aplicará ${getTrialDaysCopy()} antes del primer cobro.`}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleUpgrade}
              disabled={isActivatingSubscription}
              className="sm:flex-1"
              data-testid="activate-subscription-btn"
            >
              {isActivatingSubscription ? "Redirigiendo..." : activationCtaLabel}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(profileRoute)}
              className="sm:flex-1"
            >
              {profileCtaLabel}
            </Button>
          </div>
          <Button
            variant="link"
            className="px-0 mt-3"
            onClick={() => navigate("/account?tab=subscription")}
          >
            Ver detalles de activación <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export { ActivationGate };
